import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { Box, TextField, Card, CardContent, Typography, IconButton, Stack, Chip, Link as MuiLink } from '@mui/material';
import { Link as LinkIcon, VideoLibrary, Description, Image as ImageIcon, Close, OpenInNew } from '@mui/icons-material';

const InputLink = ({ defaultValue, rules, name, control, label, hidePreview }) => {
    const [preview, setPreview] = useState(null);
    const [linkType, setLinkType] = useState(null);

    const detectLinkType = (url) => {
        if (!url) return null;

        const videoPatterns = [
            /youtube\.com\/watch/i,
            /youtu\.be\//i,
            /vimeo\.com/i,
            /dailymotion\.com/i,
            /\.mp4$/i,
            /\.webm$/i,
            /\.ogg$/i
        ];

        const documentPatterns = [
            /\.pdf$/i,
            /\.doc$/i,
            /\.docx$/i,
            /\.xls$/i,
            /\.xlsx$/i,
            /\.ppt$/i,
            /\.pptx$/i,
            /docs\.google\.com\/document/i,
            /docs\.google\.com\/presentation/i,
            /drive\.google\.com\/file/i
        ];

        const imagePatterns = [/\.jpg$/i, /\.jpeg$/i, /\.png$/i, /\.gif$/i, /\.webp$/i, /\.svg$/i];

        if (videoPatterns.some((pattern) => pattern.test(url))) {
            return 'video';
        }
        if (documentPatterns.some((pattern) => pattern.test(url))) {
            return 'document';
        }
        if (imagePatterns.some((pattern) => pattern.test(url))) {
            return 'image';
        }
        return 'link';
    };

    const getEmbedUrl = (url) => {
        if (!url) return null;

        const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
        if (youtubeMatch) {
            return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
        }

        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }

        return url;
    };

    const generatePreview = (url) => {
        const type = detectLinkType(url);
        setLinkType(type);

        if (!url || !type) {
            setPreview(null);
            return;
        }

        switch (type) {
            case 'video':
                const embedUrl = getEmbedUrl(url);
                setPreview(
                    <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                        <iframe
                            title="Vista previa de video"
                            src={embedUrl}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 0,
                                borderRadius: '8px'
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>
                );
                break;

            case 'document':
                if (url.toLowerCase().endsWith('.pdf')) {
                    setPreview(
                        <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                            <iframe
                                title="Vista previa de documento PDF"
                                src={url}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                    borderRadius: '8px'
                                }}
                            />
                        </Box>
                    );
                } else if (url.includes('docs.google.com/document') && url.includes('/edit')) {
                    const embedUrl = url.replace('/edit', '/preview');
                    setPreview(
                        <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                            <iframe
                                title="Vista previa de documento"
                                src={embedUrl}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                    borderRadius: '8px'
                                }}
                            />
                        </Box>
                    );
                } else if (url.includes('drive.google.com/file')) {
                    const fileIdMatch = url.match(/\/file\/d\/([^/]+)/);
                    if (fileIdMatch) {
                        const fileId = fileIdMatch[1];
                        const previewUrl = `https://drive.google.com/file/d/${fileId}/preview`;
                        setPreview(
                            <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                                <iframe
                                    title="Vista previa de documento"
                                    src={previewUrl}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        border: 0,
                                        borderRadius: '8px'
                                    }}
                                />
                            </Box>
                        );
                    }
                } else {
                    setPreview(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                            <Description color="primary" />
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <Typography variant="body2" noWrap>
                                    {url}
                                </Typography>
                            </Box>
                            <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                                <OpenInNew fontSize="small" />
                            </IconButton>
                        </Box>
                    );
                }
                break;

            case 'image':
                setPreview(
                    <Box
                        component="img"
                        src={url}
                        alt="Preview"
                        sx={{
                            width: '100%',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                        }}
                    />
                );
                break;

            case 'link':
                setPreview(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                        <LinkIcon color="primary" />
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <Typography variant="body2" noWrap>
                                {url}
                            </Typography>
                        </Box>
                        <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                            <OpenInNew fontSize="small" />
                        </IconButton>
                    </Box>
                );
                break;
        }
    };

    const getLinkTypeIcon = () => {
        switch (linkType) {
            case 'video':
                return <VideoLibrary />;
            case 'document':
                return <Description />;
            case 'image':
                return <ImageIcon />;
            default:
                return <LinkIcon />;
        }
    };

    const getLinkTypeLabel = () => {
        switch (linkType) {
            case 'video':
                return 'Video';
            case 'document':
                return 'Documento';
            case 'image':
                return 'Imagen';
            default:
                return 'Enlace';
        }
    };

    useEffect(() => {
        if (defaultValue) {
            generatePreview(defaultValue);
        }
    }, []);

    return (
        <Controller
            defaultValue={defaultValue || ''}
            rules={rules || {}}
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <Box>
                    <TextField
                        {...field}
                        fullWidth
                        label={label || 'Enlace'}
                        placeholder="https://ejemplo.com"
                        error={!!error}
                        helperText={error?.message || 'Ingresa un enlace válido (YouTube, Vimeo, documentos, imágenes, etc.)'}
                        onChange={(e) => {
                            field.onChange(e);
                            generatePreview(e.target.value);
                        }}
                        InputProps={{
                            startAdornment: linkType && (
                                <Chip
                                    icon={getLinkTypeIcon()}
                                    label={getLinkTypeLabel()}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                />
                            )
                        }}
                    />

                    {!hidePreview && preview && field.value && (
                        <Card sx={{ mt: 2 }}>
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Vista previa
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            field.onChange('');
                                            setPreview(null);
                                            setLinkType(null);
                                        }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Stack>
                                {preview}
                            </CardContent>
                        </Card>
                    )}
                </Box>
            )}
        />
    );
};

InputLink.propTypes = {
    control: PropTypes.any.isRequired,
    name: PropTypes.string.isRequired,
    rules: PropTypes.object,
    defaultValue: PropTypes.string,
    label: PropTypes.string,
    hidePreview: PropTypes.bool
};

export default InputLink;
